import { Button } from "primereact/button";
import { Tag } from "primereact/tag";

import { Dialog, HubTable, ListToolbar, TableActionsMenu } from "@/ds";
import type { PapeisObraSortField } from "@/modules/recursos";

import { InativarPapelObraDialog } from "../inativar-papel-obra-dialog";
import { PapelObraFormDialog } from "../papel-obra-form-dialog";
import {
  EDIT_PAPEL_LABEL,
  INACTIVATE_PAPEL_LABEL,
  PAPEIS_OBRA_CLOSE_LABEL,
  PAPEIS_OBRA_COLUMNS,
  PAPEIS_OBRA_DIALOG_TITLE,
  PAPEIS_OBRA_EMPTY_NOUN,
  PAPEIS_OBRA_EMPTY_PLURAL,
  PAPEIS_OBRA_LOADING_LABEL,
  PAPEIS_OBRA_NEW_LABEL,
  PAPEIS_OBRA_TABLE_LABEL,
  PAPEL_SEARCH_PLACEHOLDER,
} from "./papeis-obra-dialog.constants";
import styles from "./papeis-obra-dialog.module.scss";
import type {
  PapelObraColumnKey,
  PapelObraLinha,
  PapeisObraDialogViewModel,
} from "./types-papeis-obra-dialog";

export function PapeisObraDialogView({
  search,
  buscar,
  linhas,
  total,
  status,
  errorMessage,
  table,
  hasSearch,
  limparBusca,
  tentarNovamente,
  fechar,
  novoPapel,
  editar,
  inativar,
  cadastro,
  edicao,
  inativacao,
}: PapeisObraDialogViewModel) {
  function renderCell(key: PapelObraColumnKey, linha: PapelObraLinha) {
    switch (key) {
      case "nome":
        return <span className={styles.nome}>{linha.nome}</span>;
      case "janela":
        return <span className={styles.janela}>{linha.janelaTexto}</span>;
      case "ativo":
        return (
          <Tag
            className="p-tag-xs"
            severity={linha.statusSeverity}
            value={linha.statusLabel}
            data-testid={linha.statusTestId}
          />
        );
      case "acoes":
        return (
          <TableActionsMenu.Root aria-label={linha.acoesLabel}>
            <TableActionsMenu.Trigger data-testid={linha.menuTestId} />
            <TableActionsMenu.Item
              label={EDIT_PAPEL_LABEL}
              icon="pencil"
              onClick={() => editar(linha)}
              testId={linha.editarTestId}
            />
            <TableActionsMenu.Item
              label={INACTIVATE_PAPEL_LABEL}
              icon="power-off"
              disabled={!linha.podeInativar}
              onClick={() => inativar(linha)}
              testId={linha.inativarTestId}
            />
          </TableActionsMenu.Root>
        );
    }
  }

  return (
    <>
      <Dialog
        visible
        // O Prime acharia o primeiro botão do rodapé; o foco inicial é o `autoFocus` do campo.
        focusOnShow={false}
        size="xl"
        blockScroll
        header={PAPEIS_OBRA_DIALOG_TITLE}
        onHide={fechar}
        footer={
          <div className={styles.footer}>
            <Button
              type="button"
              label={PAPEIS_OBRA_CLOSE_LABEL}
              severity="secondary"
              onClick={fechar}
              data-testid="papeis-obra-close"
            />
          </div>
        }
        data-testid="papeis-obra-dialog"
      >
        <div className={styles.corpo}>
          <ListToolbar.Root
            testId="papeis-obra-toolbar"
            framed={false}
            aria-label="Busca de papéis"
          >
            <ListToolbar.Search
              value={search}
              onChange={buscar}
              placeholder={PAPEL_SEARCH_PLACEHOLDER}
              className={styles.search}
              clearable
              testId="papeis-obra-search"
            />
            <ListToolbar.Actions>
              <Button
                type="button"
                autoFocus
                icon="pi pi-plus"
                label={PAPEIS_OBRA_NEW_LABEL}
                onClick={novoPapel}
                data-testid="papeis-obra-new"
              />
            </ListToolbar.Actions>
          </ListToolbar.Root>

          <HubTable.Root<PapelObraLinha, PapelObraColumnKey, PapeisObraSortField>
            value={linhas}
            totalRecords={total}
            table={table}
            status={status}
            errorMessage={errorMessage}
            loadingView="overlay"
            emptyView="replace"
            loadingLabel={PAPEIS_OBRA_LOADING_LABEL}
            emptyNoun={PAPEIS_OBRA_EMPTY_NOUN}
            emptyPlural={PAPEIS_OBRA_EMPTY_PLURAL}
            hasSearch={hasSearch}
            onClear={limparBusca}
            onRetry={tentarNovamente}
            onRowClick={editar}
            dataKey="id"
            dataTableProps={{
              tableStyle: { minWidth: "36rem" },
              "aria-label": PAPEIS_OBRA_TABLE_LABEL,
            }}
            testId="papeis-obra"
          >
            {PAPEIS_OBRA_COLUMNS.map((column) => (
              <HubTable.Column<PapelObraLinha, PapelObraColumnKey>
                key={column.key}
                field={column.key}
                header={column.header}
                sortable={column.sortable}
                width={column.width}
                body={(linha) => renderCell(column.key, linha)}
              />
            ))}
          </HubTable.Root>
        </div>
      </Dialog>

      {cadastro ? <PapelObraFormDialog {...cadastro} /> : null}

      {edicao ? <PapelObraFormDialog key={edicao.papel?.id} {...edicao} /> : null}

      {inativacao ? <InativarPapelObraDialog {...inativacao} /> : null}
    </>
  );
}
